import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete, Box, Button, Card, Chip, Divider, FormControlLabel, FormHelperText,
    Grid, MenuItem, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikProvider, Form } from 'formik';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import ReportsBackIcon from '../../../../assets/icons/reports-back-icon';
import { EmailChipsInput } from '../../../../components/email-chips-input';
import { ScheduledReportService } from '../../../../services/scheduledReport.service';
import { ScheduledReportTypes } from '../../../../types/scheduledReport.types';
import { scheduledReportSchema } from '../../../../schemas/scheduledReport.schema';
import {
    SOURCE_OPTIONS, WINDOW_OPTIONS, CADENCE_OPTIONS,
    DAY_OF_WEEK_OPTIONS, DAY_OF_MONTH_OPTIONS, TIME_OPTIONS, TIMEZONE_OPTIONS,
    SOURCE_LABELS, WINDOW_LABELS, defaultTimezone, describeCadence,
    WINDOWLESS_SOURCES, WINDOWLESS_HELP, FLAGGED_SOURCES,
    FILTERABLE_SOURCES, EMPTY_FILTERS, describeScope,
} from '../../../../constants/scheduledReports.constants';
import { useEffectiveSettings } from '../../../../hooks/use-effective-settings';
import { useGenericQuery } from '../../../../services/react-query/generic.query';
import { TagsService } from '../../../../services/tags-service';
import { RootState } from '../../../../store';
import { Team } from '../../../../types/teams.types';
import { Group } from '../../../../types/groups.types';
import { Tag } from '../../../../types/tags.types';

interface ContextType {
    setReportTitle: (title: string) => void;
    setReportDescText: (desc: string) => void;
}

const LIST_PATH = '/reports/scheduled-reports';
const ACCENT = '#206FA5';

const LABEL_SX = {
    fontSize: '11px', color: '#8A94A6', fontWeight: 600,
    textTransform: 'uppercase' as const, letterSpacing: '.5px',
    mb: '6px', display: 'block',
};
const CONTROL_SX = {
    '& .MuiInputBase-root': {
        fontSize: '14px', fontFamily: 'Poppins, sans-serif', borderRadius: '8px', minHeight: '46px',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E8ECF0' },
};

const Field: React.FC<{ label: string; children: React.ReactNode; error?: string | false }> = ({ label, children, error }) => (
    <Box>
        <Typography sx={LABEL_SX}>{label}</Typography>
        {children}
        {error ? <FormHelperText error sx={{ mx: 0 }}>{error}</FormHelperText> : null}
    </Box>
);

// Small section heading with an accent icon — breaks the form into Report /
// Schedule / Recipients so it doesn't read as one flat monochrome column.
const SectionHeading: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ color: ACCENT, mb: 2 }}>
        {icon}
        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>
            {children}
        </Typography>
    </Stack>
);

export const ScheduledReportFormScreen: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const { setReportTitle, setReportDescText } = useOutletContext<ContextType>();
    const [loading, setLoading] = useState(false);
    const [loadingExisting, setLoadingExisting] = useState(isEdit);
    // Flag-gated sources (e.g. the POM Program Report workbook) are offered only
    // when the customer's effective setting is on; the API enforces the same.
    const { getSetting } = useEffectiveSettings();
    const sourceOptions = SOURCE_OPTIONS.filter((o) => {
        const flag = FLAGGED_SOURCES[o.value];
        return !flag || getSetting<boolean>(flag, false) === true;
    });
    // Org scope pickers (Program Report). Same queries the POM Users filter
    // bar uses; tags come from the redux slice TagsService.fetchAll fills.
    const { data: allTeams } = useGenericQuery<Team[]>('fetchTeams', 'teams/fetchTeams', 0, 0, false);
    const { data: allGroups } = useGenericQuery<Group[]>('fetchGroups', 'groups/fetch-all-groups-teams-users', 0, 0, false);
    const allTags = useSelector((state: RootState) => state.tags.items) as Tag[];
    useEffect(() => { TagsService.fetchAll(() => undefined); }, []);

    useEffect(() => {
        setReportTitle(isEdit ? 'Edit Scheduled Report' : 'New Scheduled Report');
        setReportDescText('Choose a report, how often it should be sent, and who receives it.');
    }, [isEdit, setReportTitle, setReportDescText]);

    const formik = useFormik<ScheduledReportTypes.CreateParams>({
        initialValues: {
            name: '',
            source: 'pomUsers',
            window: 'all',
            cadence: 'weekly',
            recipients: [],
            timeOfDay: '09:00',
            timezone: defaultTimezone(),
            dayOfWeek: 1,
            dayOfMonth: 1,
            active: true,
            filters: { ...EMPTY_FILTERS },
            reportTimezone: '',
        },
        validationSchema: scheduledReportSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const done = (isValid?: boolean, msg?: string) => {
                setLoading(false);
                toast(msg, { autoClose: 1800, type: isValid ? 'success' : 'error' });
                if (isValid) navigate(LIST_PATH);
            };
            // Only filterable sources carry a scope; an empty override means
            // "render in the delivery timezone" and is omitted.
            const payload: ScheduledReportTypes.CreateParams = {
                ...values,
                filters: FILTERABLE_SOURCES.has(values.source) ? values.filters : undefined,
                reportTimezone: FILTERABLE_SOURCES.has(values.source) && values.reportTimezone ? values.reportTimezone : undefined,
            };
            if (isEdit && id) await ScheduledReportService.update(id, payload, done);
            else await ScheduledReportService.create(payload, done);
        },
    });

    const { errors, values, getFieldProps, setFieldValue, handleSubmit, submitCount } = formik;
    // Only surface validation once the user has tried to create/save — not
    // on field blur.
    const showErrors = submitCount > 0;
    const isFilterable = FILTERABLE_SOURCES.has(values.source);
    const scope = values.filters ?? EMPTY_FILTERS;
    const groupOptions = useMemo(() => (allGroups ?? []).map((g) => ({ id: g.id || (g as any)._id || '', name: g.name || '' })).filter((g) => g.id), [allGroups]);
    // Teams narrow to the chosen groups (same behaviour as the POM Users filter).
    const teamOptions = useMemo(() => {
        const groupSet = new Set(scope.groupIds ?? []);
        return (allTeams ?? [])
            .filter((t) => groupSet.size === 0 || (t.groupId && groupSet.has(t.groupId)))
            .map((t) => ({ id: t.id || (t as any)._id || '', name: t.name || '', groupId: t.groupId ?? null }))
            .filter((t) => t.id);
    }, [allTeams, scope.groupIds]);
    const tagOptions = useMemo(() => (allTags ?? []).map((t) => ({ id: t._id || '', name: t.name })).filter((t) => t.id), [allTags]);
    const groupNameById = useMemo(() => new Map(groupOptions.map((g) => [g.id, g.name])), [groupOptions]);
    const setScope = (key: keyof ScheduledReportTypes.Filters, ids: string[]) => {
        const next = { ...scope, [key]: ids };
        // Dropping a group drops any selected team that was only visible through it.
        if (key === 'groupIds' && ids.length > 0) {
            const visible = new Set((allTeams ?? []).filter((t) => t.groupId && ids.includes(t.groupId)).map((t) => t.id || (t as any)._id));
            next.teamIds = (scope.teamIds ?? []).filter((tid) => visible.has(tid));
        }
        setFieldValue('filters', next);
    };
    const pickerSx = { ...CONTROL_SX, '& .MuiInputBase-root': { ...CONTROL_SX['& .MuiInputBase-root'], minHeight: '46px', py: 0.25 } };

    useEffect(() => {
        if (!isEdit || !id) return;
        let cancelled = false;
        ScheduledReportService.fetchAll().then((all) => {
            if (cancelled) return;
            const existing = all.find((s) => s.id === id);
            if (!existing) {
                toast('Report schedule not found', { type: 'error', autoClose: 2000 });
                navigate(LIST_PATH);
                return;
            }
            formik.resetForm({
                values: {
                    name: existing.name,
                    source: existing.source,
                    window: existing.window,
                    cadence: existing.cadence,
                    recipients: existing.recipients ?? [],
                    timeOfDay: existing.timeOfDay ?? '09:00',
                    timezone: existing.timezone ?? defaultTimezone(),
                    dayOfWeek: existing.dayOfWeek ?? 1,
                    dayOfMonth: existing.dayOfMonth ?? 1,
                    active: existing.active,
                    filters: {
                        groupIds: existing.filters?.groupIds ?? [],
                        teamIds: existing.filters?.teamIds ?? [],
                        tagIds: existing.filters?.tagIds ?? [],
                    },
                    reportTimezone: existing.reportTimezone ?? '',
                },
            });
            setLoadingExisting(false);
        });
        return () => { cancelled = true; };
        // Hydrate once per id; `formik` is a stable ref and intentionally
        // excluded to avoid re-running on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    const recipientCount = values.recipients.length;
    const cadenceSentence = describeCadence(values);

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 56, mt: 2 }}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => navigate(LIST_PATH)}
                        sx={{ border: 0, p: 1, boxShadow: '0 0 2px 0 rgba(145,158,171,0.24),0 16px 32px -4px rgba(145,158,171,0.24)' }}
                    >
                        <ReportsBackIcon />
                        <Typography fontWeight={400} fontSize={14} color="black" marginLeft={1}>Back</Typography>
                    </Button>
                </Box>

                <Grid container spacing={3} sx={{ mt: 0 }}>
                    {/* Left: the form */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ p: { xs: 2.5, md: 4 } }}>
                            <SectionHeading icon={<EventRepeatOutlinedIcon fontSize="small" />}>Report</SectionHeading>
                            <Grid container columnSpacing={3} rowSpacing={3}>
                                <Grid item xs={12}>
                                    <Field label="Report Name" error={showErrors && errors.name}>
                                        <TextField
                                            fullWidth size="small" sx={CONTROL_SX}
                                            placeholder="e.g. Weekly POM Users — Leadership"
                                            disabled={loadingExisting}
                                            {...getFieldProps('name')}
                                        />
                                    </Field>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field label="Report" error={showErrors && errors.source}>
                                        <Select
                                            fullWidth size="small" sx={CONTROL_SX} disabled={loadingExisting}
                                            value={values.source}
                                            onChange={(e) => {
                                                const source = e.target.value as ScheduledReportTypes.Source;
                                                setFieldValue('source', source);
                                                // Windowless sources (POM Users roster, Program Report's fixed 7d/30d) — snap back to All.
                                                if (WINDOWLESS_SOURCES.has(source)) setFieldValue('window', 'all');
                                            }}
                                        >
                                            {sourceOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                        </Select>
                                    </Field>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field label="Date Window" error={showErrors && errors.window}>
                                        <Select
                                            fullWidth size="small" sx={CONTROL_SX}
                                            // POM Users is a current-roster snapshot — the window
                                            // doesn't apply, so lock it to "All" and say why.
                                            disabled={loadingExisting || WINDOWLESS_SOURCES.has(values.source)}
                                            {...getFieldProps('window')}
                                        >
                                            {WINDOW_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                        </Select>
                                        {!(showErrors && errors.window) && (
                                            <FormHelperText sx={{ mx: 0 }}>
                                                {WINDOWLESS_HELP[values.source] ?? 'Resolved fresh each time the report is sent.'}
                                            </FormHelperText>
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>

                            {isFilterable && (
                                <>
                                    <Divider sx={{ my: 3.5 }} />
                                    <SectionHeading icon={<FilterAltOutlinedIcon fontSize="small" />}>Limit to</SectionHeading>
                                    <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
                                        Leave everything empty to include the whole organization. Groups expand to all of their teams; when both are set, only the selected teams within the selected groups are included. Tags narrow the Users tab only.
                                    </Typography>
                                    <Grid container spacing={2.5}>
                                        <Grid item xs={12} md={4}>
                                            <Field label="Groups">
                                                <Autocomplete
                                                    multiple size="small" disabled={loadingExisting}
                                                    options={groupOptions}
                                                    getOptionLabel={(o) => o.name}
                                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                                    value={groupOptions.filter((g) => (scope.groupIds ?? []).includes(g.id))}
                                                    onChange={(_, v) => setScope('groupIds', v.map((g) => g.id))}
                                                    renderInput={(params) => <TextField {...params} placeholder={(scope.groupIds ?? []).length ? '' : 'All groups'} sx={pickerSx} />}
                                                    renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.id} />)}
                                                />
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Field label="Teams">
                                                <Autocomplete
                                                    multiple size="small" disabled={loadingExisting}
                                                    options={teamOptions}
                                                    groupBy={(scope.groupIds ?? []).length === 0 ? (o) => (o.groupId && groupNameById.get(o.groupId)) || 'No group' : undefined}
                                                    getOptionLabel={(o) => o.name}
                                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                                    value={teamOptions.filter((t) => (scope.teamIds ?? []).includes(t.id))}
                                                    onChange={(_, v) => setScope('teamIds', v.map((t) => t.id))}
                                                    renderInput={(params) => <TextField {...params} placeholder={(scope.teamIds ?? []).length ? '' : ((scope.groupIds ?? []).length ? 'Teams in selected groups' : 'All teams')} sx={pickerSx} />}
                                                    renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.id} />)}
                                                />
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Field label="Tags">
                                                <Autocomplete
                                                    multiple size="small" disabled={loadingExisting}
                                                    options={tagOptions}
                                                    getOptionLabel={(o) => o.name}
                                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                                    value={tagOptions.filter((t) => (scope.tagIds ?? []).includes(t.id))}
                                                    onChange={(_, v) => setScope('tagIds', v.map((t) => t.id))}
                                                    renderInput={(params) => <TextField {...params} placeholder={(scope.tagIds ?? []).length ? '' : 'Any tag'} sx={pickerSx} />}
                                                    renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.id} />)}
                                                />
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Field label="Report timezone (optional)">
                                                <Select
                                                    fullWidth size="small" sx={CONTROL_SX} disabled={loadingExisting} displayEmpty
                                                    value={values.reportTimezone ?? ''}
                                                    onChange={(e) => setFieldValue('reportTimezone', e.target.value)}
                                                >
                                                    <MenuItem value="">Same as delivery timezone</MenuItem>
                                                    {TIMEZONE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                                </Select>
                                                <FormHelperText sx={{ mx: 0 }}>Dates and times inside the workbook render in this zone.</FormHelperText>
                                            </Field>
                                        </Grid>
                                    </Grid>
                                </>
                            )}

                            <Divider sx={{ my: 3.5 }} />

                            <SectionHeading icon={<ScheduleOutlinedIcon fontSize="small" />}>Schedule</SectionHeading>
                            <Grid container columnSpacing={3} rowSpacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Field label="Cadence" error={showErrors && errors.cadence}>
                                        <Select fullWidth size="small" sx={CONTROL_SX} disabled={loadingExisting} {...getFieldProps('cadence')}>
                                            {CADENCE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                        </Select>
                                    </Field>
                                </Grid>

                                {values.cadence === 'weekly' && (
                                    <Grid item xs={12} md={4}>
                                        <Field label="Day of Week">
                                            <Select fullWidth size="small" sx={CONTROL_SX} disabled={loadingExisting}
                                                value={values.dayOfWeek}
                                                onChange={(e) => setFieldValue('dayOfWeek', Number(e.target.value))}>
                                                {DAY_OF_WEEK_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                            </Select>
                                        </Field>
                                    </Grid>
                                )}
                                {values.cadence === 'monthly' && (
                                    <Grid item xs={12} md={4}>
                                        <Field label="Day of Month">
                                            <Select fullWidth size="small" sx={CONTROL_SX} disabled={loadingExisting}
                                                value={values.dayOfMonth}
                                                onChange={(e) => setFieldValue('dayOfMonth', Number(e.target.value))}>
                                                {DAY_OF_MONTH_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                            </Select>
                                        </Field>
                                    </Grid>
                                )}

                                <Grid item xs={12} md={4}>
                                    <Field label="Time" error={showErrors && errors.timeOfDay}>
                                        <Select fullWidth size="small" sx={CONTROL_SX} disabled={loadingExisting} {...getFieldProps('timeOfDay')}>
                                            {TIME_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                        </Select>
                                    </Field>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Field label="Timezone" error={showErrors && errors.timezone}>
                                        <Select fullWidth size="small" sx={CONTROL_SX} disabled={loadingExisting} {...getFieldProps('timezone')}>
                                            {TIMEZONE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                        </Select>
                                    </Field>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 3.5 }} />

                            <SectionHeading icon={<GroupsOutlinedIcon fontSize="small" />}>Recipients</SectionHeading>
                            <Field label="Recipient Emails">
                                <EmailChipsInput
                                    label=""
                                    value={values.recipients}
                                    onChange={(emails) => setFieldValue('recipients', emails)}
                                    error={Boolean(showErrors && errors.recipients)}
                                    helperText={
                                        (showErrors && (errors.recipients as string)) ||
                                        'These addresses receive the report — they don’t need POM logins.'
                                    }
                                />
                            </Field>

                            <FormControlLabel
                                sx={{ mt: 3 }}
                                control={
                                    <Switch
                                        checked={values.active}
                                        disabled={loadingExisting}
                                        onChange={(e) => setFieldValue('active', e.target.checked)}
                                    />
                                }
                                label="Active"
                            />
                        </Card>
                    </Grid>

                    {/* Right: live summary — fills the space and adds a color block */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ p: 3, bgcolor: '#F4F8FC', border: '1px solid #E1EAF3', boxShadow: 'none', position: 'sticky', top: 16 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ color: ACCENT, mb: 2 }}>
                                <ScheduleOutlinedIcon fontSize="small" />
                                <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>
                                    Summary
                                </Typography>
                            </Stack>

                            <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: 'text.primary' }}>
                                Sends the{' '}
                                <Box component="span" sx={{ fontWeight: 700 }}>{SOURCE_LABELS[values.source]}</Box>{' '}
                                report{' '}
                                <Box component="span" sx={{ fontWeight: 700 }}>{cadenceSentence}</Box>
                                {' '}to{' '}
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                    {recipientCount} recipient{recipientCount === 1 ? '' : 's'}
                                </Box>.
                            </Typography>

                            <Divider sx={{ my: 2.5, borderColor: '#E1EAF3' }} />

                            <Stack spacing={1.25}>
                                {[
                                    ['Report', SOURCE_LABELS[values.source]],
                                    ['Date window', WINDOW_LABELS[values.window]],
                                    ...(isFilterable ? [['Scope', describeScope(scope)]] : []),
                                    ['Status', values.active ? 'Active' : 'Inactive'],
                                ].map(([k, v]) => (
                                    <Stack key={k} direction="row" justifyContent="space-between">
                                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{k}</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{v}</Typography>
                                    </Stack>
                                ))}
                            </Stack>

                            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                <LoadingButton
                                    type="submit" variant="contained" loading={loading} disabled={loadingExisting} fullWidth
                                    sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#2B5A87' } }}
                                >
                                    {isEdit ? 'Save Changes' : 'Create Report'}
                                </LoadingButton>
                                <Button variant="outlined" color="inherit" onClick={() => navigate(LIST_PATH)}>
                                    Cancel
                                </Button>
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>
            </Form>
        </FormikProvider>
    );
};

export default ScheduledReportFormScreen;
